import React from 'react';
import { useDrag } from 'react-dnd';
import { Item } from '../../types/Item';

// Helper function to format price - shows "€ op maat" for items with price 0
const formatPrice = (price: number): string => {
  if (price === 0) {
    return '€ op maat';
  }
  return `€${price.toFixed(2)}`;
};

/* ==================== DRAGGABLE ITEM ==================== */
interface DraggableItemProps {
  item: Item;
  onMove: (item: Item) => void;
}

export const DraggableItem: React.FC<DraggableItemProps> = ({ item, onMove }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'item',
    item: item,
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (draggedItem: any, monitor: any) => {
      if (monitor.didDrop()) {
        onMove(draggedItem);
      }
    },
  }));

  return (
    <div
      ref={drag}
      className={`draggable-item ${isDragging ? 'dragging' : ''}`}
    >
      <div className="item-content">
        <div className="item-name">{item.name}</div>
        <div className="item-price">{formatPrice(item.price)}</div>
      </div>
      <div className="drag-handle">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 6H6V8H8V6Z" fill="#999"/>
          <path d="M8 10H6V12H8V10Z" fill="#999"/>
          <path d="M8 14H6V16H8V14Z" fill="#999"/>
          <path d="M14 6H12V8H14V6Z" fill="#999"/>
          <path d="M14 10H12V12H14V10Z" fill="#999"/>
          <path d="M14 14H12V16H14V14Z" fill="#999"/>
          <path d="M20 6H18V8H20V6Z" fill="#999"/>
          <path d="M20 10H18V12H20V10Z" fill="#999"/>
          <path d="M20 14H18V16H20V14Z" fill="#999"/>
        </svg>
      </div>
    </div>
  );
};

/* ==================== CANVAS ITEM ==================== */
interface CanvasItemProps {
  item: Item;
  onRemove: () => void;
}

export const CanvasItem: React.FC<CanvasItemProps> = ({ item, onRemove }) => {
  // Check if item name is long (more than 8 characters)
  const isLongText = item.name.length > 8;
  
  return (
    <div className="canvas-item">
      <div className="item-header">
        <button 
          className="remove-button"
          onClick={onRemove}
          title="Item verwijderen"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      <div className={`canvas-item-content ${isLongText ? 'long-text' : ''}`}>
        <h4 className="item-name">{item.name}</h4>
        <div className="item-price">{formatPrice(item.price)}</div>
      </div>
    </div>
  );
};
